import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const ABOUT_HERO = "/images/about-hero.webp";
const ABOUT_COMMUNITY = "/images/about-community.webp";
const ABOUT_WHY = "/images/about-why-cabo.webp";

const JSONLD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Cabo Unstoppable",
  url: "https://cabounstoppable.com",
  logo: "https://cabounstoppable.com/images/logo.png",
  slogan: "BE UNSTOPPABLE",
  description:
    "Cabo Unstoppable is a premium lifestyle and apparel brand born in Los Cabos, Baja California Sur, Mexico. Inspired by the energy, ambition and resilience of the Los Cabos community, the brand creates apparel and lifestyle products for those who keep moving forward.",
  email: "info@cabounstoppable.com",
  foundingLocation: {
    "@type": "Place",
    name: "Los Cabos",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Los Cabos",
      addressRegion: "Baja California Sur",
      addressCountry: "MX",
    },
  },
  sameAs: ["https://www.instagram.com/cabounstoppable/"],
};

export default function About() {
  const { t } = useLanguage();
  const a = t.about;

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "about-jsonld";
    script.textContent = JSON.stringify(JSONLD);
    document.head.appendChild(script);
    return () => script.remove();
  }, []);

  const pillars = [
    { num: "01", title: a.pillar1Title, copy: a.pillar1Copy },
    { num: "02", title: a.pillar2Title, copy: a.pillar2Copy },
    { num: "03", title: a.pillar3Title, copy: a.pillar3Copy },
  ];

  return (
    <main data-testid="about-page" className="bg-black min-h-screen">
      {/* 01 — Hero (preserved) */}
      <section className="relative min-h-[70vh] flex items-end pt-28 md:pt-32 pb-20">
        <div className="absolute inset-0">
          <img
            src={ABOUT_HERO}
            alt={a.imgAltHero}
            className="w-full h-full object-cover object-[62%_50%] md:object-center image-cinematic"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/35 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
        </div>
        <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-10 w-full">
          <p className="overline mb-4">{a.overline}</p>
          <h1 className="h-display text-white text-5xl sm:text-7xl md:text-8xl lg:text-9xl break-words">
            {a.title}
            <br />
            <span className="text-yellow-400">{a.title2}</span>
          </h1>
        </div>
      </section>

      {/* 02 — What Cabo Unstoppable is */}
      <section
        data-testid="about-brand-intro"
        className="max-w-[1500px] mx-auto px-6 md:px-10 py-20 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center"
      >
        <div className="lg:col-span-7 min-w-0">
          <div className="w-12 h-[3px] bg-yellow-400 mb-8" />
          <h2 className="h-display text-white text-4xl sm:text-5xl md:text-6xl break-words">
            {a.brandName}
          </h2>
          <p className="mt-8 text-lg md:text-xl leading-relaxed text-white/75 max-w-2xl">
            {a.introP1}
          </p>
          <p className="mt-10 font-display uppercase tracking-tight leading-tight text-2xl md:text-3xl text-white">
            {a.introP2a}{" "}
            <span className="text-yellow-400">{a.introP2b}</span>
          </p>
        </div>
        <div className="lg:col-span-5">
          <div className="aspect-[2/3] overflow-hidden border border-white/10">
            <img
              src={ABOUT_COMMUNITY}
              alt={a.imgAltIntro}
              className="w-full h-full object-cover image-cinematic"
            />
          </div>
        </div>
      </section>

      {/* 03 — Editorial statement */}
      <section
        data-testid="about-statement"
        className="border-y border-white/10"
      >
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-16 md:py-24">
          <h2 className="h-display text-4xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] break-words">
            <span className="text-white">{a.statement1}</span>
            <br />
            <span className="text-yellow-400">{a.statement2}</span>
          </h2>
        </div>
      </section>

      {/* 04 — Why Cabo */}
      <section data-testid="about-why-cabo">
        <div className="relative min-h-[60vh] md:h-[75vh] md:max-h-[820px] flex items-end">
          <div className="absolute inset-0">
            <img
              src={ABOUT_WHY}
              alt={a.imgAltWhy}
              className="w-full h-full object-cover object-[50%_36%] image-cinematic"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/10" />
          </div>
          <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-10 w-full pb-14 md:pb-20 pt-40">
            <p className="overline mb-4">{a.whyOverline}</p>
            <h2 className="h-display text-white text-4xl sm:text-6xl md:text-7xl break-words">
              {a.whyTitle1}
              <br />
              <span className="text-yellow-400">{a.whyTitle2}</span>
            </h2>
          </div>
        </div>
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="hidden lg:block lg:col-span-4">
            <p className="font-mono text-[11px] tracking-[0.3em] text-white/40 uppercase">
              22.89° N · 109.92° W
              <br />
              LOS CABOS · BCS · MX
            </p>
          </div>
          <div className="lg:col-span-7 min-w-0">
            <p className="text-lg md:text-xl leading-relaxed text-white/75">
              {a.whyP1}
            </p>
            <p className="mt-8 font-display uppercase tracking-tight text-2xl md:text-3xl text-white leading-tight">
              {a.whyP2}
            </p>
            <p className="mt-8 text-lg md:text-xl leading-relaxed text-white/75">
              {a.whyP3}
            </p>
            <div className="mt-10 border-l-2 border-yellow-400 pl-6">
              <p className="text-lg md:text-xl leading-relaxed text-white">
                {a.whyP4}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 05 — Brand manifesto 01/02/03 */}
      <section data-testid="about-manifesto" className="bg-zinc-950">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="border-t border-white/10">
            {pillars.map((p) => (
              <div
                key={p.num}
                data-testid={`about-pillar-${p.num}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-10 py-10 md:py-14 border-b border-white/10"
              >
                <div className="md:col-span-2">
                  <span className="font-display text-5xl md:text-6xl text-yellow-400 leading-none">
                    {p.num}
                  </span>
                </div>
                <div className="md:col-span-4 min-w-0">
                  <h3 className="h-display text-white text-2xl md:text-3xl break-words">
                    {p.title}
                  </h3>
                </div>
                <div className="md:col-span-6 min-w-0">
                  <p className="text-white/60 leading-relaxed text-base md:text-lg">
                    {p.copy}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 07 — Final brand statement */}
      <section
        data-testid="about-final-statement"
        className="relative py-24 md:py-36"
      >
        <div className="max-w-[1500px] mx-auto px-6 md:px-10">
          <p className="font-mono text-[11px] tracking-[0.3em] text-white/40 uppercase mb-8">
            LOS CABOS · BCS · MX
          </p>
          <h2 className="h-display text-5xl sm:text-7xl md:text-8xl leading-[0.95] break-words">
            <span className="text-white">{a.finalTitle1}</span>
            <br />
            <span className="text-yellow-400">{a.finalTitle2}</span>
          </h2>
          <div className="mt-12 max-w-2xl">
            <p className="text-lg md:text-xl leading-relaxed text-white/75">
              {a.finalP1}
            </p>
            <p className="mt-6 text-lg md:text-xl leading-relaxed text-white/75">
              {a.finalP2}
            </p>
          </div>
          <p className="mt-14 h-display text-yellow-400 text-3xl md:text-4xl">
            {a.finalMark}
          </p>
          <div className="mt-12 pt-10 border-t border-white/10">
            <Link
              to="/product/cabo-unstoppable-cap"
              data-testid="about-final-cta"
              className="cta-line inline-flex items-center gap-2 text-white text-xs font-bold uppercase tracking-[0.25em]"
            >
              {a.finalCta}
              <ArrowUpRight className="btn-arrow w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
